import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { DashboardLayout, Card, Button } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import {
  adminApi,
  ConflictDurationSettings,
  ConflictSettings
} from '../services/api';

type DurationKey = keyof ConflictDurationSettings;
type DurationUnit = 'days' | 'seconds';

const unitSeconds: Record<DurationUnit, number> = {
  days: 24 * 60 * 60,
  seconds: 1
};

const unitLabelKeys: Record<DurationUnit, string> = {
  days: 'adminConflictSettings.units.daysLabel',
  seconds: 'adminConflictSettings.units.secondsTest'
};

const defaultSettings: ConflictSettings = {
  nodeEnv: 'production',
  testTimeUnitsAvailable: false,
  runtimeWarnings: [],
  settingDefinitions: {} as ConflictSettings['settingDefinitions'],
  lastChangedBy: null,
  lastChangedAt: null,
  lastChangeReason: null,
  inactivityFirstNoticeSeconds: 7 * 24 * 60 * 60,
  inactivityReminderSeconds: 14 * 24 * 60 * 60,
  inactivityResolutionSeconds: 21 * 24 * 60 * 60
};

const fallbackMinimums: Record<DurationKey, number> = {
  inactivityFirstNoticeSeconds: 7 * 24 * 60 * 60,
  inactivityReminderSeconds: 14 * 24 * 60 * 60,
  inactivityResolutionSeconds: 21 * 24 * 60 * 60
};

const fieldLabelKeys: Record<DurationKey, string> = {
  inactivityFirstNoticeSeconds: 'adminConflictSettings.fields.inactivityFirstNotice',
  inactivityReminderSeconds: 'adminConflictSettings.fields.inactivityReminder',
  inactivityResolutionSeconds: 'adminConflictSettings.fields.inactivityResolution'
};

const durationKeys = Object.keys(fieldLabelKeys) as DurationKey[];

const preferredUnit = (seconds: number, allowSeconds: boolean): DurationUnit => {
  if (seconds % unitSeconds.days === 0) return 'days';
  return allowSeconds ? 'seconds' : 'days';
};

const initialUnits = (settings: ConflictSettings): Record<DurationKey, DurationUnit> =>
  durationKeys.reduce((units, key) => {
    units[key] = preferredUnit(settings[key], settings.testTimeUnitsAvailable);
    return units;
  }, {} as Record<DurationKey, DurationUnit>);

const formatNumber = (value: number) =>
  new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 4 }).format(value);

const formatDuration = (seconds: number, t: TFunction) => {
  const unit = preferredUnit(seconds, true);
  const value = seconds / unitSeconds[unit];
  const label = unit === 'days'
    ? t('adminConflictSettings.units.day', { count: value })
    : t('adminConflictSettings.units.second', { count: value });
  return `${formatNumber(value)} ${label}`;
};

type DurationFieldProps = {
  settingKey: DurationKey;
  value: number;
  unit: DurationUnit;
  allowSeconds: boolean;
  description: string;
  onValueChange: (value: number) => void;
  onUnitChange: (unit: DurationUnit) => void;
};

function DurationField({
  settingKey,
  value,
  unit,
  allowSeconds,
  description,
  onValueChange,
  onUnitChange
}: DurationFieldProps) {
  const { t } = useTranslation();
  const inputValue = value / unitSeconds[unit];
  const units: DurationUnit[] = allowSeconds ? ['days', 'seconds'] : ['days'];

  return (
    <div>
      <label className="form-label fw-semibold" htmlFor={settingKey}>
        {t(fieldLabelKeys[settingKey])}
      </label>
      <div className="input-group">
        <input
          id={settingKey}
          className="form-control"
          type="number"
          min={1 / unitSeconds[unit]}
          step="any"
          value={inputValue}
          onChange={event => {
            const next = Number(event.target.value);
            onValueChange(Number.isFinite(next) ? Math.round(next * unitSeconds[unit]) : 0);
          }}
        />
        <select
          className="form-select"
          style={{ maxWidth: 180 }}
          aria-label={t('adminConflictSettings.fields.unitAriaLabel', {
            field: t(fieldLabelKeys[settingKey])
          })}
          value={unit}
          onChange={event => onUnitChange(event.target.value as DurationUnit)}
        >
          {units.map(option => (
            <option key={option} value={option}>{t(unitLabelKeys[option])}</option>
          ))}
        </select>
      </div>
      <div className="form-text">{description}</div>
    </div>
  );
}

export function AdminConflictSettings() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<ConflictSettings>(defaultSettings);
  const [units, setUnits] = useState<Record<DurationKey, DurationUnit>>(
    initialUnits(defaultSettings)
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [unsafeAcknowledged, setUnsafeAcknowledged] = useState(false);
  const [unsafeReason, setUnsafeReason] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  useEffect(() => {
    adminApi.getConflictSettings()
      .then(loaded => {
        const merged = { ...defaultSettings, ...loaded };
        setSettings(merged);
        setUnits(initialUnits(merged));
      })
      .catch(() => setSaveMessage({
        type: 'error',
        text: t('adminConflictSettings.errors.load')
      }))
      .finally(() => setIsLoading(false));
  }, [t]);

  const unsafeSettings = useMemo(() => {
    return durationKeys.filter(key => settings[key] < (
      settings.settingDefinitions[key]?.recommendedMinimum ?? fallbackMinimums[key]
    ));
  }, [settings]);

  const requiresProductionReason = settings.nodeEnv === 'production' && unsafeSettings.length > 0;
  const needsWarningAcknowledgement = isDirty && !unsafeAcknowledged && (
    unsafeSettings.length > 0 ||
    settings.runtimeWarnings.some(warning => warning.kind === 'simulation')
  );

  const recommendedMinimums = useMemo(
    () => durationKeys.reduce((minimums, key) => {
      minimums[key] = settings.settingDefinitions[key]?.recommendedMinimum ?? fallbackMinimums[key];
      return minimums;
    }, {} as Record<DurationKey, number>),
    [settings.settingDefinitions]
  );

  const updateValue = (key: DurationKey, value: number) => {
    setSettings(previous => ({ ...previous, [key]: value }));
    setIsDirty(true);
    setUnsafeAcknowledged(false);
    setSaveMessage(null);
  };

  const handleSave = async () => {
    if (durationKeys.some(key => !Number.isInteger(settings[key]) || settings[key] <= 0)) {
      setSaveMessage({ type: 'error', text: t('adminConflictSettings.errors.allPositive') });
      return;
    }
    if (
      settings.inactivityFirstNoticeSeconds >= settings.inactivityReminderSeconds ||
      settings.inactivityReminderSeconds >= settings.inactivityResolutionSeconds
    ) {
      setSaveMessage({
        type: 'error',
        text: t('adminConflictSettings.errors.order')
      });
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);
    try {
      const saved = await adminApi.updateConflictSettings({
        inactivityFirstNoticeSeconds: settings.inactivityFirstNoticeSeconds,
        inactivityReminderSeconds: settings.inactivityReminderSeconds,
        inactivityResolutionSeconds: settings.inactivityResolutionSeconds,
        ...(requiresProductionReason
          ? { unsafeProductionConfirmed: unsafeAcknowledged, reason: unsafeReason.trim() }
          : {})
      });
      setSettings(saved);
      setIsDirty(false);
      setSaveMessage({ type: 'success', text: t('adminConflictSettings.saved') });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch {
      setSaveMessage({ type: 'error', text: t('adminConflictSettings.errors.save') });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <Breadcrumb items={[
        { label: t('adminConflictSettings.breadcrumb.system'), path: '/admin/system-menu' },
        { label: t('adminConflictSettings.breadcrumb.current') }
      ]} />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">{t('adminConflictSettings.title')}</h4>
          <div className="text-muted small">
            {t('adminConflictSettings.subtitle')}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {saveMessage && (
            <span className={`text-${saveMessage.type === 'success' ? 'success' : 'danger'} small`}>
              {saveMessage.text}
            </span>
          )}
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={
              isSaving ||
              isLoading ||
              needsWarningAcknowledgement ||
              (requiresProductionReason &&
                (!unsafeAcknowledged || !unsafeReason.trim()))
            }
          >
            {isSaving ? t('adminConflictSettings.actions.saving') : t('adminConflictSettings.actions.save')}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status" />
        </div>
      ) : (
        <div className="d-flex flex-column gap-4">
          {settings.testTimeUnitsAvailable && (
            <div className="alert alert-info mb-0" role="alert">
              <strong>{t('adminConflictSettings.testEnv.title')}</strong>{' '}
              {t('adminConflictSettings.testEnv.body')}
              {settings.runtimeWarnings.length > 0 && (
                <ul className="mb-0 mt-2">
                  {settings.runtimeWarnings.map(warning => (
                    <li key={warning.key}>{warning.title}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <Card>
            <h5 className="mb-1">{t('adminConflictSettings.inactivity.title')}</h5>
            <p className="text-muted mb-4">
              {t('adminConflictSettings.inactivity.desc')}
            </p>
            <div className="alert alert-secondary" role="note">
              <div className="fw-semibold mb-2">{t('adminConflictSettings.inactivity.autoTitle')}</div>
              <ul className="mb-2 ps-3">
                <li>
                  {t('adminConflictSettings.inactivity.autoSeller')}
                </li>
                <li>
                  {t('adminConflictSettings.inactivity.autoBuyer')}
                </li>
              </ul>
              {t('adminConflictSettings.inactivity.autoBody')}
            </div>
            <div className="row g-4">
              {([
                ['inactivityFirstNoticeSeconds', 'adminConflictSettings.help.firstNotice'],
                ['inactivityReminderSeconds', 'adminConflictSettings.help.reminder'],
                ['inactivityResolutionSeconds', 'adminConflictSettings.help.resolution']
              ] as Array<[DurationKey, string]>).map(([key, descriptionKey]) => (
                <div className="col-lg-4" key={key}>
                  <DurationField
                    settingKey={key}
                    value={settings[key]}
                    unit={units[key]}
                    allowSeconds={settings.testTimeUnitsAvailable}
                    description={t(descriptionKey)}
                    onValueChange={value => updateValue(key, value)}
                    onUnitChange={unit => setUnits(previous => ({ ...previous, [key]: unit }))}
                  />
                </div>
              ))}
            </div>
            <div className="form-text mt-3">
              {t('adminConflictSettings.help.inactivityFootnote')}
            </div>
          </Card>
        </div>
      )}

      {needsWarningAcknowledgement && (
        <div
          className="modal d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.72)', zIndex: 1100 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="unsafe-conflict-settings-title"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border border-danger border-2">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title" id="unsafe-conflict-settings-title">
                  {t('adminConflictSettings.warning.title')}
                </h5>
              </div>
              <div className="modal-body">
                <p className="fw-semibold">
                  {t('adminConflictSettings.warning.intro')}
                </p>
                <ul className="mb-3">
                  {unsafeSettings.map(key => {
                    const warning = settings.runtimeWarnings.find(item => item.key === key);
                    return (
                      <li key={key}>
                        {t('adminConflictSettings.warning.item', {
                          label: t(fieldLabelKeys[key]),
                          actual: formatDuration(settings[key], t),
                          recommended: formatDuration(recommendedMinimums[key], t)
                        })}
                        {warning?.changedAt && (
                          <div className="small text-muted">
                            {t('adminConflictSettings.warning.changed', {
                              environment: warning.environment,
                              actor: warning.changedBy || t('adminConflictSettings.warning.unknownActor'),
                              changedAt: new Date(warning.changedAt).toLocaleString('sv-SE')
                            })}
                          </div>
                        )}
                        {warning?.changeReason && (
                          <div className="small">
                            {t('adminConflictSettings.warning.reason', {
                              reason: warning.changeReason
                            })}
                          </div>
                        )}
                      </li>
                    );
                  })}
                  {settings.runtimeWarnings
                    .filter(warning => warning.kind === 'simulation')
                    .map(warning => (
                      <li key={warning.key}>
                        <strong>{warning.title}</strong>: {warning.message}{' '}
                        {warning.disableInstructions}
                        {warning.changedAt && (
                          <> {t('adminConflictSettings.warning.changed', {
                            environment: warning.environment,
                            actor: warning.changedBy || t('adminConflictSettings.warning.unknownActor'),
                            changedAt: new Date(warning.changedAt).toLocaleString('sv-SE')
                          })}</>
                        )}
                      </li>
                    ))}
                </ul>
                <p className="mb-0">
                  {t('adminConflictSettings.warning.body')}
                </p>
                {requiresProductionReason && (
                  <div className="mt-3">
                    <label className="form-label fw-semibold" htmlFor="unsafe-settings-reason">
                      {t('adminConflictSettings.warning.reasonLabel')}
                    </label>
                    <textarea
                      id="unsafe-settings-reason"
                      className="form-control"
                      maxLength={2000}
                      value={unsafeReason}
                      onChange={event => setUnsafeReason(event.target.value)}
                    />
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-danger"
                  disabled={requiresProductionReason && !unsafeReason.trim()}
                  onClick={() => setUnsafeAcknowledged(true)}
                >
                  {t('adminConflictSettings.warning.acknowledge')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
